import { db } from '../firebase';
import { 
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  Timestamp,
  runTransaction
} from 'firebase/firestore';
import { QueueManagerService } from './queueManagerService';
import { SystemSettingsService } from './systemSettingsService';
import { executeTemplates as executeTemplatesV2 } from './templateExecutionServiceV2';
import { getOpenAISettings } from './openaiServiceV2';
import { getFieldValue } from '../utils/templateUtils';

const WORKFLOWS = 'workflows';
const WORKFLOW_EXECUTIONS = 'workflow_executions';
const TEMPLATES = 'templates';

export const WorkflowServiceV2 = {
  // Create a new workflow
  createWorkflow: async (userId, workflowData) => {
    const settings = await SystemSettingsService.getSettings();
    
    // Check workflow limit
    const userWorkflowsQuery = query(
      collection(db, WORKFLOWS),
      where('userId', '==', userId)
    );
    const userWorkflows = await getDocs(userWorkflowsQuery);
    
    if (userWorkflows.size >= settings.maxWorkflowsPerUser) {
      throw new Error('Maximum number of workflows reached');
    }

    // Validate workflow steps
    if (!workflowData.templateIds || workflowData.templateIds.length === 0) {
      throw new Error('Workflow must have at least one template');
    }
    
    if (workflowData.templateIds.length > settings.maxWorkflowSteps) {
      throw new Error('Maximum number of workflow steps exceeded');
    }

    // Verify all templates exist and user has access
    await Promise.all(workflowData.templateIds.map(async templateId => {
      const templateRef = doc(db, TEMPLATES, templateId);
      const templateDoc = await getDoc(templateRef);
      if (!templateDoc.exists() || templateDoc.data().userId !== userId) {
        throw new Error(`Template ${templateId} not found or access denied`);
      }
    }));

    const workflowRef = doc(collection(db, WORKFLOWS));
    const workflow = {
      id: workflowRef.id,
      userId,
      name: workflowData.name,
      description: workflowData.description || '',
      templates: workflowData.templateIds,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      version: 2 // Mark as V2 workflow
    };

    await setDoc(workflowRef, workflow);
    return workflow;
  },

  // Execute a workflow on multiple products
  executeWorkflow: async (userId, workflow, products, options = {}) => {
    const workflowId = typeof workflow === 'string' ? workflow : workflow.id;
    console.log('Executing workflow V2:', { workflowId, productCount: products.length, stopMarkers: options.stopMarkers });
    
    const settings = await SystemSettingsService.getSettings();
    
    // Clean up old/stuck workflow executions first
    await WorkflowServiceV2.cleanupStuckExecutions(userId);
    
    // Check concurrent workflows limit after cleanup
    const runningWorkflowsQuery = query(
      collection(db, WORKFLOW_EXECUTIONS),
      where('userId', '==', userId),
      where('status', '==', 'running')
    );
    const runningWorkflows = await getDocs(runningWorkflowsQuery);
    
    console.log(`Found ${runningWorkflows.size} running workflows for user ${userId}`);
    
    // If max concurrent workflows reached, queue the workflow instead of throwing error
    if (runningWorkflows.size >= settings.maxConcurrentWorkflows) {
      console.log(`Max concurrent workflows reached (${runningWorkflows.size}/${settings.maxConcurrentWorkflows}). Queueing workflow...`);
      return await WorkflowServiceV2.queueWorkflow(userId, workflow, products, options);
    }

    // If workflow is a string (ID), fetch it from the database
    let workflowData;
    if (typeof workflow === 'string') {
      const workflowRef = doc(db, WORKFLOWS, workflow);
      const workflowDoc = await getDoc(workflowRef);
      if (!workflowDoc.exists() || workflowDoc.data().userId !== userId) {
        throw new Error('Workflow not found or access denied');
      }
      workflowData = workflowDoc.data();
    } else {
      // Use the provided workflow object
      workflowData = workflow;
    }

    // Create execution record
    const executionRef = doc(collection(db, WORKFLOW_EXECUTIONS));
    const executionId = executionRef.id;
    
    // Store product IDs for execution tracking
    const productIds = products.map(p => {
      if (typeof p === 'object' && p.id) {
        return p.id.toString();
      }
      return p.toString();
    });
    
    // Store full product data for resume functionality
    const productsData = products.map(p => {
      if (typeof p === 'object') {
        return p; // Store full product object
      }
      return { id: p.toString() }; // If only ID, store as object with id
    });
    
    const execution = {
      id: executionId,
      workflowId: workflowData.id,
      workflowName: workflowData.name,
      userId,
      shopId: options.shopId || null,
      status: 'running',
      progress: 0,
      startTime: Timestamp.now(),
      endTime: null,
      productIds,
      productsData, // Store full product objects for resume
      productCount: products.length,
      completedProducts: 0,
      results: {},
      error: null,
      version: 2, // Mark as V2 execution
      stopMarkers: options.stopMarkers || [], // Array of template indices where to pause
      isPaused: false,
      pausedAt: null,
      pausedAtTemplate: null
    };
    
    await setDoc(executionRef, execution);
    
    // Start execution in background
    setTimeout(async () => {
      try {
        const openAISettings = await getOpenAISettings(userId);
        const templates = workflowData.templates || [];
        const templateCount = templates.length;
        let currentTemplateIndex = 0;
        
        // Load existing results if resuming from a pause
        let executionResults = execution.results || {};
        
        // Process each template in sequence
        for (let templateIdx = 0; templateIdx < templates.length; templateIdx++) {
          const templateId = templates[templateIdx];
          // Force update the template index to match the loop index
          // This ensures accurate tracking of which template we're processing
          currentTemplateIndex = templateIdx;
          
          console.log(`DEBUG: Processing template ${templateIdx + 1}/${templateCount}: ${templateId}`);
          
          // Check if execution was cancelled
          const currentExecution = await getDoc(executionRef);
          if (!currentExecution.exists() || currentExecution.data().status === 'cancelled') {
            console.log('Workflow execution cancelled');
            return;
          }
          
          // Create a fixed copy of the current template index for this operation
          // This ensures consistent template index display throughout the template processing
          const fixedTemplateIndex = currentTemplateIndex;
          
          console.log(`DEBUG: Starting template ${templateId}, using fixed index ${fixedTemplateIndex + 1} of ${templateCount}`);

          // Update progress - show starting progress for current template
          const templateProgress = fixedTemplateIndex / templateCount;
          await updateDoc(executionRef, { 
            progress: templateProgress,
            message: `Processing template ${fixedTemplateIndex + 1} of ${templateCount}`
          });
          
          // Call progress callback
          if (options.onProgress) {
            options.onProgress({
              executionId,
              status: 'running',
              progress: templateProgress * 100, // Convert to percentage
              message: `Processing template ${fixedTemplateIndex + 1} of ${templateCount}`,
              currentTemplateIndex: fixedTemplateIndex + 1, // Add explicit template index for UI
              totalTemplateCount: templateCount, // Add explicit total for UI
              results: executionResults
            });
          }
          
          // Execute template
          console.log(`Executing template ${fixedTemplateIndex + 1}/${templateCount}: ${templateId}`);
          
          try {
            // Use QueueManagerService to manage concurrent API calls
            await QueueManagerService.addToQueue(async () => {
              // Use the original products passed to the workflow
              // Do NOT use execution.productsData as it may have stale/incorrect data
              const productsToUse = products;
              
              const templateResults = await executeTemplatesV2(
                [templateId],
                productsToUse,
                [], // templates array (will be loaded by ID)
                {
                  userId: userId,
                  shopId: options.shopId,
                  getFieldValue,
                  // Pass the correct workflow context for template indexing
                  // Use fixedTemplateIndex + 1 to get the 1-based index for display
                  workflowContext: {
                    currentTemplateIndex: fixedTemplateIndex + 1, 
                    totalTemplates: templateCount,
                    templateId: templateId,
                    debugIndex: templateIdx // Include the original loop index for debugging
                  },
                  onComplete: (results) => {
                    console.log(`Template ${templateId} execution complete`);
                  },
                  onProgress: (progressData) => {
                    // Create a fixed copy of the current template index for this closure
                    // This ensures the progress callback always uses the correct index
                    const fixedTemplateIndex = currentTemplateIndex;
                    
                    // Handle both old format (number) and new format (object)
                    const progressValue = typeof progressData === 'number' ? progressData : progressData.progress;
                    
                    // Calculate overall progress - each template contributes equally to total progress
                    // progress is now a percentage (0-100), so we use it directly
                    const templateBaseProgress = (fixedTemplateIndex / templateCount) * 100;
                    const templateContribution = progressValue / templateCount;
                    const overallProgress = templateBaseProgress + templateContribution;
                    
                    // Ensure progress doesn't exceed 100%
                    const safeProgress = Math.min(overallProgress, 100);
                    
                    // Create detailed message with product and template info
                    let detailedMessage;
                    if (typeof progressData === 'object' && progressData.message) {
                      detailedMessage = progressData.message;
                    } else {
                      detailedMessage = `Template ${fixedTemplateIndex + 1}/${templateCount}: ${Math.round(progressValue)}% complete`;
                    }
                    
                    console.log(`Progress: ${detailedMessage} (${safeProgress.toFixed(1)}% overall)`);
                    
                    // Update execution record with detailed message
                    updateDoc(executionRef, { 
                      progress: safeProgress / 100, // Store as decimal in DB
                      message: detailedMessage
                    }).catch(err => console.warn('Progress update failed:', err));
                    
                    // Call progress callback with detailed information
                    if (options.onProgress) {
                      options.onProgress({
                        executionId,
                        status: 'running',
                        progress: safeProgress, // Pass as percentage
                        message: detailedMessage,
                        productInfo: typeof progressData === 'object' ? {
                          currentProduct: progressData.currentProduct,
                          totalProducts: progressData.totalProducts,
                          productTitle: progressData.productTitle
                        } : null,
                        templateInfo: typeof progressData === 'object' ? {
                          currentTemplate: progressData.currentTemplate,
                          totalTemplates: progressData.totalTemplates,
                          templateName: progressData.templateName
                        } : null,
                        results: executionResults
                      });
                    }
                  }
                }
              );
              
              // Merge results properly - deep merge template results for each product
              Object.entries(templateResults || {}).forEach(([productId, productTemplateResults]) => {
                if (!executionResults[productId]) {
                  executionResults[productId] = {};
                }
                
                // Merge template results for this product
                Object.entries(productTemplateResults || {}).forEach(([templateId, templateResult]) => {
                  executionResults[productId][templateId] = templateResult;
                });
              });
              
              console.log('Merged execution results after template:', {
                templateId,
                productCount: Object.keys(executionResults).length,
                totalTemplateResults: Object.values(executionResults).reduce((total, productResults) => 
                  total + Object.keys(productResults || {}).length, 0
                )
              });
              
              // CRITICAL: Notify after each template completion so UI can process results with insert mode
              // This ensures body_html is properly mapped to descriptionHtml with the correct insert mode
              if (options.onTemplateComplete) {
                options.onTemplateComplete({
                  templateId,
                  templateIndex: fixedTemplateIndex,
                  results: templateResults
                });
              }
            });
          } catch (error) {
            console.error(`Error executing template ${templateId}:`, error);
            
            // Update execution with error - using the fixedTemplateIndex for consistency
            await updateDoc(executionRef, {
              status: 'error',
              error: `Error in template ${fixedTemplateIndex + 1}: ${error.message}`,
              endTime: Timestamp.now()
            });
            
            // Call progress callback with error
            if (options.onProgress) {
              options.onProgress({
                executionId,
                status: 'error',
                progress: ((fixedTemplateIndex + 0.5) / templateCount) * 100, // Convert to percentage
                message: `Error in template ${fixedTemplateIndex + 1}: ${error.message}`,
                currentTemplateIndex: fixedTemplateIndex + 1, // Add explicit template index for UI
                totalTemplateCount: templateCount, // Add explicit total for UI
                results: executionResults,
                error: error.message
              });
            }
            
            // Call error callback if provided
            if (options.onError) {
              options.onError({
                executionId,
                status: 'error',
                message: `Error in template ${fixedTemplateIndex + 1}: ${error.message}`,
                error: error.message
              });
            }
            
            throw error;
          }
          
          // We no longer need to manually increment currentTemplateIndex
          // It's now controlled by the for loop index (templateIdx)
          console.log(`DEBUG: Completed template ${templateId}, index ${fixedTemplateIndex + 1}/${templateCount}`);
          
          // Check if this template has a stop marker
          const executionData = await getDoc(executionRef);
          const stopMarkers = executionData.data()?.stopMarkers || [];
          const hasStopMarker = stopMarkers.includes(fixedTemplateIndex);
          
          if (hasStopMarker) {
            console.log(`🛑 Stop marker detected at template ${fixedTemplateIndex + 1}. Pausing workflow...`);
            
            // Save current execution results before pausing
            console.log('Saving execution results before pause:', {
              productCount: Object.keys(executionResults).length,
              templateCount: Object.values(executionResults).reduce((total, productResults) => 
                total + Object.keys(productResults || {}).length, 0
              )
            });
            
            // Update execution to paused state with current results
            await updateDoc(executionRef, {
              isPaused: true,
              pausedAt: Timestamp.now(),
              pausedAtTemplate: fixedTemplateIndex + 1,
              status: 'paused',
              results: executionResults, // Save results so far
              message: `Paused at template ${fixedTemplateIndex + 1} of ${templateCount} - Review results and click Continue`
            });
            
            // Call progress callback to inform about paused state
            if (options.onProgress) {
              options.onProgress({
                executionId,
                status: 'paused',
                progress: ((fixedTemplateIndex + 1) / templateCount) * 100,
                message: `Paused at template ${fixedTemplateIndex + 1} of ${templateCount}`,
                currentTemplateIndex: fixedTemplateIndex + 1,
                totalTemplateCount: templateCount,
                results: executionResults,
                isPaused: true
              });
            }
            
            // Wait for resume signal
            console.log('Waiting for resume signal...');
            let isPausedState = true;
            while (isPausedState) {
              await new Promise(resolve => setTimeout(resolve, 1000)); // Check every second
              const currentExecution = await getDoc(executionRef);
              
              if (!currentExecution.exists()) {
                console.log('Execution document deleted, stopping workflow');
                return;
              }
              
              const execData = currentExecution.data();
              
              // Check if cancelled
              if (execData.status === 'cancelled') {
                console.log('Workflow cancelled during pause');
                return;
              }
              
              // Check if resumed
              if (!execData.isPaused || execData.status === 'running') {
                console.log('✅ Workflow resumed, continuing...');
                console.log('Resuming with execution results:', {
                  productCount: Object.keys(executionResults).length,
                  templateCount: Object.values(executionResults).reduce((total, productResults) => 
                    total + Object.keys(productResults || {}).length, 0
                  )
                });
                isPausedState = false;
                
                // Update status back to running
                await updateDoc(executionRef, {
                  status: 'running',
                  message: `Resuming from template ${fixedTemplateIndex + 1}...`
                });
                
                // Call progress callback WITHOUT results to avoid re-processing
                // The UI already has the results from before the pause
                if (options.onProgress) {
                  options.onProgress({
                    executionId,
                    status: 'running',
                    progress: ((fixedTemplateIndex + 1) / templateCount) * 100,
                    message: `Resuming from template ${fixedTemplateIndex + 1}...`,
                    currentTemplateIndex: fixedTemplateIndex + 1,
                    totalTemplateCount: templateCount,
                    // DO NOT pass results here - it causes processTemplateResults to re-process old results!
                    isPaused: false
                  });
                }
              }
            }
          }
          
          // No need to increment - the loop will handle this
        }
        
        // Update execution as completed
        await updateDoc(executionRef, {
          status: 'completed',
          progress: 1,
          results: executionResults,
          endTime: Timestamp.now(),
          completedProducts: products.length,
          message: 'Workflow execution completed successfully'
        });
        
        // Call completion callback
        if (options.onComplete) {
          options.onComplete({
            executionId,
            status: 'completed',
            progress: 1,
            results: executionResults,
            message: 'Workflow execution completed successfully'
          });
        }
        
        // Call progress callback with completion
        if (options.onProgress) {
          options.onProgress({
            executionId,
            status: 'completed',
            progress: 100, // 100% completion
            results: executionResults,
            message: 'Workflow execution completed successfully'
          });
        }
        
        return {
          executionId,
          results: executionResults
        };
      } catch (error) {
        console.error('Error executing workflow:', error);
        
        // Update execution with error if not already updated
        try {
          const currentExecution = await getDoc(executionRef);
          if (currentExecution.exists() && currentExecution.data().status === 'running') {
            await updateDoc(executionRef, {
              status: 'error',
              error: error.message,
              endTime: Timestamp.now()
            });
          }
        } catch (updateError) {
          console.error('Error updating execution status:', updateError);
        }
        
        // Call error callback if provided
        if (options.onError) {
          options.onError({
            executionId,
            status: 'error',
            message: error.message,
            error: error.message
          });
        }
        
        throw error;
      }
    }, 0);
    
    // Return execution ID immediately
    return executionId;
  },

  // Get workflow execution status
  getExecutionStatus: async (userId, executionId) => {
    const executionRef = doc(db, WORKFLOW_EXECUTIONS, executionId);
    const executionDoc = await getDoc(executionRef);
    
    if (!executionDoc.exists() || executionDoc.data().userId !== userId) {
      throw new Error('Workflow execution not found or access denied');
    }
    
    return executionDoc.data();
  },

  // Cancel workflow execution
  cancelExecution: async (userId, executionId) => {
    const executionRef = doc(db, WORKFLOW_EXECUTIONS, executionId);
    
    await runTransaction(db, async (transaction) => {
      const executionDoc = await transaction.get(executionRef);
      
      if (!executionDoc.exists() || executionDoc.data().userId !== userId) {
        throw new Error('Workflow execution not found or access denied');
      }
      
      const execution = executionDoc.data();
      
      if (execution.status !== 'running' && execution.status !== 'queued') {
        throw new Error('Cannot cancel a completed or failed workflow');
      }
      
      transaction.update(executionRef, {
        status: 'cancelled',
        endTime: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
      
      // Cancel any pending tasks
      // This will be handled by the QueueManagerService
    });
    
    return { success: true };
  },

  // Clean up stuck workflow executions
  cleanupStuckExecutions: async (userId) => {
    const settings = await SystemSettingsService.getSettings();
    const timeoutMinutes = settings.workflowTimeoutMinutes || 60;
    const cutoffTime = new Date(Date.now() - (timeoutMinutes * 60 * 1000));
    
    console.log(`Cleaning up stuck executions older than ${timeoutMinutes} minutes for user ${userId}`);
    
    // Find running executions that are older than the timeout
    const stuckExecutionsQuery = query(
      collection(db, WORKFLOW_EXECUTIONS),
      where('userId', '==', userId),
      where('status', '==', 'running')
    );
    
    const stuckExecutions = await getDocs(stuckExecutionsQuery);
    const cleanupPromises = [];
    
    stuckExecutions.forEach(doc => {
      const execution = doc.data();
      const startTime = execution.startTime?.toDate() || new Date(0);
      
      if (startTime < cutoffTime) {
        console.log(`Marking stuck execution as timed out: ${doc.id}, started: ${startTime}`);
        cleanupPromises.push(
          updateDoc(doc.ref, {
            status: 'timeout',
            error: `Workflow timed out after ${timeoutMinutes} minutes`,
            endTime: Timestamp.now()
          })
        );
      }
    });
    
    if (cleanupPromises.length > 0) {
      await Promise.all(cleanupPromises);
      console.log(`Cleaned up ${cleanupPromises.length} stuck workflow executions`);
    } else {
      console.log('No stuck workflow executions found');
    }
    
    return cleanupPromises.length;
  },

  // Queue a workflow when max concurrent limit is reached
  queueWorkflow: async (userId, workflow, products, options = {}) => {
    const workflowData = typeof workflow === 'string' ? 
      await WorkflowServiceV2.getWorkflow(userId, workflow) : workflow;

    // Create execution record with 'queued' status
    const executionRef = doc(collection(db, WORKFLOW_EXECUTIONS));
    const executionId = executionRef.id;
    
    const execution = {
      id: executionId,
      workflowId: workflowData.id,
      workflowName: workflowData.name,
      userId,
      shopId: options.shopId || null,
      status: 'queued',
      progress: 0,
      startTime: null, // Will be set when execution actually starts
      queuedAt: Timestamp.now(),
      endTime: null,
      productIds: products.map(p => p.toString()),
      productCount: products.length,
      completedProducts: 0,
      results: {},
      error: null,
      version: 2,
      // Store workflow data and options for later execution
      queuedWorkflowData: workflowData,
      queuedProducts: products,
      queuedOptions: options
    };
    
    await setDoc(executionRef, execution);
    
    console.log(`Workflow queued with execution ID: ${executionId}`);
    
    // Try to process queue immediately
    setTimeout(() => WorkflowServiceV2.processQueue(userId), 1000);
    
    // Call progress callback to inform about queued status
    if (options.onProgress) {
      options.onProgress({
        executionId,
        status: 'queued',
        progress: 0,
        message: 'Workflow queued - waiting for available slot...',
        results: {}
      });
    }
    
    return executionId;
  },

  // Process queued workflows when slots become available
  processQueue: async (userId) => {
    const settings = await SystemSettingsService.getSettings();
    
    // Check current running workflows
    const runningWorkflowsQuery = query(
      collection(db, WORKFLOW_EXECUTIONS),
      where('userId', '==', userId),
      where('status', '==', 'running')
    );
    const runningWorkflows = await getDocs(runningWorkflowsQuery);
    
    const availableSlots = settings.maxConcurrentWorkflows - runningWorkflows.size;
    
    if (availableSlots <= 0) {
      console.log('No available workflow slots for queue processing');
      return;
    }
    
    // Get queued workflows (oldest first)
    const queuedWorkflowsQuery = query(
      collection(db, WORKFLOW_EXECUTIONS),
      where('userId', '==', userId),
      where('status', '==', 'queued'),
      orderBy('queuedAt', 'asc'),
      limit(availableSlots)
    );
    const queuedWorkflows = await getDocs(queuedWorkflowsQuery);
    
    console.log(`Processing ${queuedWorkflows.size} queued workflows (${availableSlots} slots available)`);
    
    // Start queued workflows
    const startPromises = queuedWorkflows.docs.map(async (doc) => {
      const execution = doc.data();
      
      try {
        // Update status to running and set start time
        await updateDoc(doc.ref, {
          status: 'running',
          startTime: Timestamp.now(),
          message: 'Starting workflow execution...'
        });
        
        console.log(`Starting queued workflow: ${execution.id}`);
        
        // Execute the workflow using stored data
        await WorkflowServiceV2.executeWorkflowDirect(
          execution.id,
          execution.queuedWorkflowData,
          execution.queuedProducts,
          execution.queuedOptions
        );
        
      } catch (error) {
        console.error(`Error starting queued workflow ${execution.id}:`, error);
        
        // Update execution with error
        await updateDoc(doc.ref, {
          status: 'error',
          error: `Failed to start queued workflow: ${error.message}`,
          endTime: Timestamp.now()
        });
      }
    });
    
    await Promise.all(startPromises);
  },

  // Direct workflow execution (used internally for queued workflows)
  executeWorkflowDirect: async (executionId, workflowData, products, options = {}) => {
    const executionRef = doc(db, WORKFLOW_EXECUTIONS, executionId);
    
    try {
      const openAISettings = await getOpenAISettings(options.userId || workflowData.userId);
      const templates = workflowData.templates || [];
      const templateCount = templates.length;
      let currentTemplateIndex = 0;
      let executionResults = {};
      
      // Process each template in sequence (same logic as original executeWorkflow)
      for (let templateIdx = 0; templateIdx < templates.length; templateIdx++) {
        const templateId = templates[templateIdx];
        currentTemplateIndex = templateIdx;
        
        console.log(`DEBUG: Processing template ${templateIdx + 1}/${templateCount}: ${templateId}`);
        
        // Check if execution was cancelled
        const currentExecution = await getDoc(executionRef);
        if (!currentExecution.exists() || currentExecution.data().status === 'cancelled') {
          console.log('Workflow execution cancelled');
          return;
        }
        
        const fixedTemplateIndex = currentTemplateIndex;
        
        // Update progress
        const templateProgress = fixedTemplateIndex / templateCount;
        await updateDoc(executionRef, { 
          progress: templateProgress,
          message: `Processing template ${fixedTemplateIndex + 1} of ${templateCount}`
        });
        
        // Call progress callback
        if (options.onProgress) {
          options.onProgress({
            executionId,
            status: 'running',
            progress: templateProgress * 100,
            message: `Processing template ${fixedTemplateIndex + 1} of ${templateCount}`,
            currentTemplateIndex: fixedTemplateIndex + 1,
            totalTemplateCount: templateCount,
            results: executionResults
          });
        }
        
        // Execute template (same logic as original)
        try {
          await QueueManagerService.addToQueue(async () => {
            const templateResults = await executeTemplatesV2(
              [templateId],
              products,
              [],
              {
                userId: options.userId || workflowData.userId,
                shopId: options.shopId,
                getFieldValue,
                workflowContext: {
                  currentTemplateIndex: fixedTemplateIndex + 1, 
                  totalTemplates: templateCount,
                  templateId: templateId,
                  debugIndex: templateIdx
                },
                onComplete: (results) => {
                  console.log(`Template ${templateId} execution complete`);
                },
                onProgress: (progressData) => {
                  const fixedTemplateIndex = currentTemplateIndex;
                  const progressValue = typeof progressData === 'number' ? progressData : progressData.progress;
                  const templateBaseProgress = (fixedTemplateIndex / templateCount) * 100;
                  const templateContribution = progressValue / templateCount;
                  const overallProgress = templateBaseProgress + templateContribution;
                  const safeProgress = Math.min(overallProgress, 100);
                  
                  let detailedMessage;
                  if (typeof progressData === 'object' && progressData.message) {
                    detailedMessage = progressData.message;
                  } else {
                    detailedMessage = `Template ${fixedTemplateIndex + 1}/${templateCount}: ${Math.round(progressValue)}% complete`;
                  }
                  
                  console.log(`Progress: ${detailedMessage} (${safeProgress.toFixed(1)}% overall)`);
                  
                  updateDoc(executionRef, { 
                    progress: safeProgress / 100,
                    message: detailedMessage
                  }).catch(err => console.warn('Progress update failed:', err));
                  
                  if (options.onProgress) {
                    options.onProgress({
                      executionId,
                      status: 'running',
                      progress: safeProgress,
                      message: detailedMessage,
                      productInfo: typeof progressData === 'object' ? {
                        currentProduct: progressData.currentProduct,
                        totalProducts: progressData.totalProducts,
                        productTitle: progressData.productTitle
                      } : null,
                      templateInfo: typeof progressData === 'object' ? {
                        currentTemplate: progressData.currentTemplate,
                        totalTemplates: progressData.totalTemplates,
                        templateName: progressData.templateName
                      } : null,
                      results: executionResults
                    });
                  }
                }
              }
            );
            
            // Merge results
            Object.entries(templateResults || {}).forEach(([productId, productTemplateResults]) => {
              if (!executionResults[productId]) {
                executionResults[productId] = {};
              }
              Object.entries(productTemplateResults || {}).forEach(([templateId, templateResult]) => {
                executionResults[productId][templateId] = templateResult;
              });
            });
          });
        } catch (error) {
          console.error(`Error executing template ${templateId}:`, error);
          
          await updateDoc(executionRef, {
            status: 'error',
            error: `Error in template ${fixedTemplateIndex + 1}: ${error.message}`,
            endTime: Timestamp.now()
          });
          
          if (options.onError) {
            options.onError({
              executionId,
              status: 'error',
              message: `Error in template ${fixedTemplateIndex + 1}: ${error.message}`,
              error: error.message
            });
          }
          
          throw error;
        }
      }
      
      // Update execution as completed
      await updateDoc(executionRef, {
        status: 'completed',
        progress: 1,
        results: executionResults,
        endTime: Timestamp.now(),
        completedProducts: products.length,
        message: 'Workflow execution completed successfully'
      });
      
      // Call completion callback
      if (options.onComplete) {
        options.onComplete({
          executionId,
          status: 'completed',
          progress: 1,
          results: executionResults,
          message: 'Workflow execution completed successfully'
        });
      }
      
      if (options.onProgress) {
        options.onProgress({
          executionId,
          status: 'completed',
          progress: 100,
          results: executionResults,
          message: 'Workflow execution completed successfully'
        });
      }
      
      // Process queue for next workflows
      setTimeout(() => WorkflowServiceV2.processQueue(options.userId || workflowData.userId), 1000);
      
      return {
        executionId,
        results: executionResults
      };
      
    } catch (error) {
      console.error('Error executing workflow:', error);
      
      try {
        const currentExecution = await getDoc(executionRef);
        if (currentExecution.exists() && currentExecution.data().status === 'running') {
          await updateDoc(executionRef, {
            status: 'error',
            error: error.message,
            endTime: Timestamp.now()
          });
        }
      } catch (updateError) {
        console.error('Error updating execution status:', updateError);
      }
      
      if (options.onError) {
        options.onError({
          executionId,
          status: 'error',
          message: error.message,
          error: error.message
        });
      }
      
      // Process queue even after error
      setTimeout(() => WorkflowServiceV2.processQueue(options.userId || workflowData.userId), 1000);
      throw error;
    }
  },

  // Resume a paused workflow execution
  resumeExecution: async (userId, executionId) => {
    const executionRef = doc(db, WORKFLOW_EXECUTIONS, executionId);
    
    await runTransaction(db, async (transaction) => {
      const executionDoc = await transaction.get(executionRef);
      
      if (!executionDoc.exists() || executionDoc.data().userId !== userId) {
        throw new Error('Workflow execution not found or access denied');
      }
      
      const execution = executionDoc.data();
      
      if (execution.status !== 'paused') {
        throw new Error('Cannot resume a workflow that is not paused');
      }
      
      transaction.update(executionRef, {
        isPaused: false,
        status: 'running',
        updatedAt: Timestamp.now()
      });
    });
    
    console.log(`✅ Workflow execution ${executionId} resumed`);
    return { success: true };
  },

  // Get workflow by ID (helper function)
  getWorkflow: async (userId, workflowId) => {
    const workflowRef = doc(db, WORKFLOWS, workflowId);
    const workflowDoc = await getDoc(workflowRef);
    if (!workflowDoc.exists() || workflowDoc.data().userId !== userId) {
      throw new Error('Workflow not found or access denied');
    }
    return workflowDoc.data();
  }
};
